import { PlusOutlined, SettingOutlined } from "@ant-design/icons"
import { Button, Tabs } from "antd"
import React, { Suspense, lazy, useCallback, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
  useSearchParams
} from "react-router-dom"

import { RoutesPaths } from "../../constants/routes.ts"
import {
  type CampaignPrefill,
  type ComposerPrefill,
  HUB_PARAMS,
  HubActionsProvider,
  type HubActions,
  type SettingsSection,
  isSettingsSection
} from "./hub/hub-context.ts"
import { type HubRights, useHubRights } from "./hub/hub-rights.ts"
import type { ContactRef } from "./types/compose.types.ts"

const ComposerModal = lazy(() => import("./composer/ComposerModal.tsx"))
const CampaignWizard = lazy(
  () => import("./campaigns/wizard/CampaignWizard.tsx")
)
const SettingsDrawer = lazy(() => import("./settings/SettingsDrawer.tsx"))
const ContactPreferencesDrawer = lazy(
  () => import("./consent/ContactPreferencesDrawer.tsx")
)
const SetupGuideModal = lazy(() => import("./setup/SetupGuideModal.tsx"))

export interface CommunicationSection {
  key: string
  labelKey: string
  isVisible: (rights: HubRights) => boolean
}

export const COMMUNICATION_SECTIONS: CommunicationSection[] = [
  {
    key: RoutesPaths.communication.overview,
    labelKey: "commHub.tabs.overview",
    isVisible: (rights) => rights.readsMessages
  },
  {
    key: RoutesPaths.communication.messages,
    labelKey: "commHub.tabs.messages",
    isVisible: (rights) => rights.readsMessages
  },
  {
    key: RoutesPaths.communication.optOuts,
    labelKey: "commHub.tabs.optOuts",
    isVisible: (rights) => rights.viewAll
  },
  {
    key: RoutesPaths.communication.campaigns,
    labelKey: "commHub.tabs.campaigns",
    isVisible: (rights) => rights.readsCampaigns
  },
  {
    key: RoutesPaths.communication.automations,
    labelKey: "commHub.tabs.automations",
    isVisible: (rights) => rights.automationsView
  },
  {
    key: RoutesPaths.communication.templates,
    labelKey: "commHub.tabs.templates",
    isVisible: (rights) => rights.templatesView
  }
]

export const CommunicationHome = ({
  fallback
}: {
  fallback: React.ReactNode
}) => {
  const rights = useHubRights()
  const first = COMMUNICATION_SECTIONS.find((section) =>
    section.isVisible(rights)
  )
  return first ? <Navigate to={first.key} replace /> : <>{fallback}</>
}

const CommunicationLayout = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [params, setParams] = useSearchParams()
  const rights = useHubRights()
  const [composer, setComposer] = useState<ComposerPrefill | null>(null)
  const [campaign, setCampaign] = useState<CampaignPrefill | null>(null)

  const sections = COMMUNICATION_SECTIONS.filter((section) =>
    section.isVisible(rights)
  )
  const active = sections.find((section) =>
    pathname.startsWith(section.key)
  )?.key

  const settingsParam = params.get(HUB_PARAMS.settings)
  const settingsSection: SettingsSection | null =
    rights.settings && settingsParam !== null
      ? isSettingsSection(settingsParam)
        ? settingsParam
        : "general"
      : null
  const preferencesOpen =
    rights.preferencesView && params.get(HUB_PARAMS.preferences) !== null
  const setupOpen = params.get(HUB_PARAMS.setup) !== null

  const setParam = useCallback(
    (
      key: string,
      value: string | null,
      extra?: Record<string, string | null>
    ) =>
      setParams(
        (current) => {
          const next = new URLSearchParams(current)
          const apply = (k: string, v: string | null) =>
            v === null ? next.delete(k) : next.set(k, v)
          apply(key, value)
          Object.entries(extra ?? {}).forEach(([k, v]) => apply(k, v))
          return next
        },
        { replace: false }
      ),
    [setParams]
  )

  const actions = useMemo<HubActions>(
    () => ({
      openComposer: (prefill) => setComposer(prefill ?? {}),
      openCampaignWizard: (prefill) => setCampaign(prefill ?? {}),
      openSettings: (section) =>
        setParam(HUB_PARAMS.settings, section ?? "general"),
      openContactPreferences: (contact?: ContactRef) =>
        setParam(HUB_PARAMS.preferences, "", {
          [HUB_PARAMS.contact]: contact ? `${contact.type}:${contact.id}` : null
        }),
      openSetup: () => setParam(HUB_PARAMS.setup, "")
    }),
    [setParam]
  )

  return (
    <HubActionsProvider value={actions}>
      <div className="flex flex-col gap-4 px-4 py-5 sm:px-6 [&_:is(h1,h2,h3)]:text-left">
        <header className="flex flex-wrap items-start justify-between gap-4">
          <div className="text-start">
            <h1 className="m-0 p-0 text-left text-[28px] font-semibold leading-tight text-slate-900">
              {t("commHub.title")}
            </h1>
            <p className="m-0 mt-1 text-base text-slate-600">
              {t("commHub.tagline")}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {rights.settings && (
              <Button
                size="large"
                icon={<SettingOutlined aria-hidden />}
                onClick={() =>
                  actions.openSettings(settingsSection ?? "general")
                }
              >
                {t("commHub.header.settings")}
              </Button>
            )}
            {rights.send && (
              <Button
                size="large"
                type="primary"
                icon={<PlusOutlined aria-hidden />}
                onClick={() => actions.openComposer()}
              >
                {t("commHub.header.newMessage")}
              </Button>
            )}
          </div>
        </header>
        {sections.length > 0 && (
          <Tabs
            className="comm-hub-tabs"
            activeKey={active ?? ""}
            onChange={(key) => navigate(key)}
            items={sections.map((section) => ({
              key: section.key,
              label: t(section.labelKey)
            }))}
          />
        )}
        <Outlet />
      </div>
      <Suspense fallback={null}>
        {composer && (
          <ComposerModal prefill={composer} onClose={() => setComposer(null)} />
        )}
        {campaign && (
          <CampaignWizard
            prefill={campaign}
            onClose={() => setCampaign(null)}
          />
        )}
        {settingsSection && (
          <SettingsDrawer
            section={settingsSection}
            onSectionChange={(section) =>
              setParam(HUB_PARAMS.settings, section)
            }
            onClose={() => setParam(HUB_PARAMS.settings, null)}
          />
        )}
        {preferencesOpen && (
          <ContactPreferencesDrawer
            contactParam={params.get(HUB_PARAMS.contact)}
            onSelectContact={(key) => setParam(HUB_PARAMS.contact, key)}
            onClose={() =>
              setParam(HUB_PARAMS.preferences, null, {
                [HUB_PARAMS.contact]: null
              })
            }
          />
        )}
        {setupOpen && (
          <SetupGuideModal onClose={() => setParam(HUB_PARAMS.setup, null)} />
        )}
      </Suspense>
    </HubActionsProvider>
  )
}

export default CommunicationLayout
